import React, { useEffect, useMemo, useState } from 'react'
import { Button, Chip } from '@mui/material'
import { Switch } from 'antd'
import { toast } from 'react-toastify'
import { getPermissions } from 'src/utils/Common'
import { permitsList } from 'src/constants/permits'
import { getSync, getConfig, setConfig, procesarSync } from 'src/services/preciosService'
import TimelineDetalle from './TimelineDetalle'
import TablaPrecios from './TablaPrecios'

const chipEstado = (estado) => {
  const map = {
    ok: { color: 'success', txt: 'OK' },
    pendiente: { color: 'warning', txt: 'Pendiente' },
    error: { color: 'error', txt: 'Error' },
  }
  const s = map[estado]
  // Sin tarea NO es lo mismo que pendiente: para ese material nunca se encoló nada. Antes caía al
  // default y se pintaba de amarillo, así que 3.080 materiales gritaban igual que los 53 que sí
  // tienen un error de verdad — y el ruido tapaba justo lo que hay que mirar.
  if (!s) {
    return <span style={{ color: '#9aa5b1', fontSize: 12 }} title="Este material nunca se ha sincronizado">—</span>
  }
  return <Chip size="small" color={s.color} label={s.txt} variant="outlined" />
}

const CANALES_AUTO = [
  { sistema: 'ia', label: 'Agente IA' },
  { sistema: 'impulsa', label: 'Impulsa' },
  { sistema: 'ecommerce', label: 'Ecommerce/Vtex' },
]

const SyncTab = ({ onChanged }) => {
  const [data, setData] = useState([])
  const [config, setConfigState] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const permisos = getPermissions() || {}
  const isAdmin = Boolean(permisos[permitsList.PRECIOS_ADMIN])

  const fetchData = async () => {
    setIsLoading(true)
    const [sync, cfg] = await Promise.all([getSync(), getConfig()])
    if (sync && sync.success) setData(sync.data)
    if (cfg && cfg.success) {
      const map = {}
      cfg.data.forEach((c) => {
        map[c.clave] = c.valor
      })
      setConfigState(map)
    }
    setIsLoading(false)
  }

  useEffect(() => {
    fetchData()
  }, [])

  const toggleAuto = async (sistema, checked) => {
    const res = await setConfig(`auto_${sistema}`, checked ? '1' : '0')
    if (res && res.success) {
      setConfigState({ ...config, [`auto_${sistema}`]: checked ? '1' : '0' })
    } else {
      toast.error('No se pudo cambiar la configuración.')
    }
  }

  const procesar = async () => {
    const res = await procesarSync()
    if (res && res.success) {
      toast.success(`${res.procesadas} tarea(s) procesada(s) (simulado, sin envío externo).`)
      fetchData()
      onChanged?.()
    } else {
      toast.error('No se pudo procesar.')
    }
  }

  const columns = useMemo(
    () => [
      { accessorKey: 'material', header: 'Material' },
      { id: 'modelo', header: 'Modelo', accessorFn: (r) => `${r.marca} ${r.modelo}` },
      { accessorKey: 'sap', header: 'SAP', Cell: ({ cell }) => chipEstado(cell.getValue()) },
      { accessorKey: 'ia', header: 'Agente IA', Cell: ({ cell }) => chipEstado(cell.getValue()) },
      { accessorKey: 'ecommerce', header: 'Ecommerce/Vtex', Cell: ({ cell }) => chipEstado(cell.getValue()) },
      { accessorKey: 'impulsa', header: 'Impulsa', Cell: ({ cell }) => chipEstado(cell.getValue()) },
    ],
    [],
  )

  return (
    <div>
      {isAdmin && (
        <div style={{ border: '1px solid #e1e5ea', borderRadius: 10, padding: '12px 16px', marginBottom: 14, background: '#fafbfc' }}>
          <strong style={{ fontSize: 13 }}>Automatización (simulada, sin envío externo)</strong>
          <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center', marginTop: 10 }}>
            {CANALES_AUTO.map((c) => (
              <label key={c.sistema} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
                <Switch
                  size="small"
                  checked={config[`auto_${c.sistema}`] === '1'}
                  onChange={(checked) => toggleAuto(c.sistema, checked)}
                />
                {c.label}
              </label>
            ))}
            <Button size="small" variant="contained" onClick={procesar}>
              Procesar automáticos ahora
            </Button>
          </div>
          <p style={{ fontSize: 11.5, color: '#6b7684', margin: '8px 0 0' }}>
            Los canales encendidos se marcan al “Procesar”. SAP sigue por publicación del plano.
          </p>
        </div>
      )}

      <p style={{ fontSize: 12.5, color: '#6b7684' }}>
        Solo lectura. Verde: el precio confirmado es el PVP vigente; si cambia el PVP, vuelve a amarillo.
        Expande una fila (▸) para ver la <strong>línea de tiempo</strong>.
      </p>
      <TablaPrecios
        columns={columns}
        data={data}
        isLoading={isLoading}
        pageSize={20}
        renderDetailPanel={({ row }) => <TimelineDetalle material={row.original.material} />}
      />
    </div>
  )
}

export default SyncTab
